import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { notify, Toast } from "../../common/Toast/index.js";
import FormFields from "../../common/FormFields/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const Terms = () => {
  const permission = getUserMenuPermission(5);
  const [isTermsData, setIsTermsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    term_Id: 0,
    name: "",
  });

  const userId = localStorage.getItem("userId");

  const fields = [
    {
      name: "name",
      label: "Term Name",
      type: "text",
      required: true,
      errorMessage: "Term name is required",
    },
  ];
  const fetchTermsData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `Terms/GetAll?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      setIsTermsData(res.data.terms);
      setFilteredData(res.data.terms);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(Math.ceil(res.data.totalRecords / pageSize));
    } catch (err) {
      console.error("Error fetching data:", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTermsData();
  }, [currentPage]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddTerms = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add Terms.", "error");
      return;
    }
    setFormData({
      term_Id: "",
      name: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditTerms = async (id) => {
    try {
      const { data } = await axiosInstance.get(`Terms/GetById/${id}`);
      if (data) {
        setFormData({
          term_Id: data.term_Id || "",
          name: data.term_Name || "",
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      notify("Error fetching Term data:", err);
    }
  };
  const handleDeleteTerms = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`Terms/Delete/${selectedId}`);
      fetchTermsData();
      notify("Term deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Term!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    try {
      const payload = isEditing
        ? {
          term_Id: formData.term_Id ? parseInt(formData.term_Id, 10) : 0,
          term_Name: formData.name,
          modified_By: userId,
        }
        : {
          term_Name: formData.name,
          created_By: userId,
        };

      await axiosInstance.post("Terms/SaveUpdate", payload);
      fetchTermsData();
      setIsModelOpen(false);
      notify(
        isEditing ? "Term updated successfully!" : "Term added successfully!",
        "success"
      );
    } catch (err) {
      notify("Error submitting form:", err);
    }
  };
  const fetchFilteredTerms = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `Terms/FilterByName?termName=${query}`
      );
      setFilteredData(res.data);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchTermsData();
    } else {
      fetchFilteredTerms(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchTermsData();
  };
  return (
    <>
      <Toast />
      <div >
        <div className="rounded-3xl bg-transparent px-2 pt-2 pb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Terms & Conditions</h2>
              <p className="text-sm text-gray-500 font-medium">Manage standard contractual terms and clauses</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-[300px]">
                <input
                  type="text"
                  className="w-full bg-white border border-gray-100 py-2.5 pl-10 pr-10 rounded-xl focus:ring-2 focus:ring-primary/10 focus:border-primary outline-none text-sm shadow-sm transition-all"
                  placeholder="Search terms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary/60">
                  <LiaSearchPlusSolid className="w-5 h-5" />
                </button>
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400 hover:text-red-500 transition-colors"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                )}
              </div>
              {permission.isInsert && (
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/95 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                  onClick={handleAddTerms}
                >
                  <span>+</span> Add Term
                </button>
              )}
            </div>
          </div>

          <div className="premium-card overflow-hidden border-none shadow-xl shadow-gray-200/50 max-w-4xl">
            {loading ? (
              <div className="p-20 flex justify-center"><Loader /></div>
            ) : permission.isView ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[#f9fafc] text-[#405189] uppercase tracking-wider text-[11px] font-bold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">Term Description</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {filteredData.length > 0 ? (
                      filteredData.map((terms) => (
                        <tr
                          key={terms.term_Id}
                          className="hover:bg-blue-50/30 transition-colors group cursor-pointer"
                        >
                          <td className="px-6 py-4 font-medium text-gray-700 leading-relaxed">
                            {terms.term_Name}
                          </td>
                          <td className="px-6 py-4 w-[120px]">
                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {permission.isUpdate && (
                                <button
                                  className="p-2 bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleEditTerms(terms.term_Id); }}
                                  title="Edit Term"
                                >
                                  <BiSolidEditAlt className="w-4 h-4" />
                                </button>
                              )}
                              {permission.isDelete && (
                                <button
                                  className="p-2 bg-gray-50 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteTerms(terms.term_Id); }}
                                  title="Delete Term"
                                >
                                  <MdDeleteForever className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center py-20 text-gray-400 font-medium italic">
                          No terms found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-red-500 text-lg py-20 font-bold bg-white">
                Access Denied: You don't have permission to view terms.
              </div>
            )}

            <div className="bg-[#f9fafc] px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-[13px] text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">
                  {isTermsData?.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalRecords)}
                </span> of{" "}
                <span className="font-bold text-gray-800">{totalRecords}</span> records
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Terms" : "Add Terms"}
      >
        <div className="p-1">
          <FormFields
            onSubmit={handleSubmit}
            setIsModelOpen={setIsModelOpen}
            formData={formData}
            handleInputChange={handleInputChange}
            fields={fields}
            isEditing={isEditing}
            onClear={() => setIsModelOpen(false)}
          />
        </div>
      </Model>

      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdDeleteForever className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Delete</h3>
          <p className="text-gray-500 mb-8 max-w-[300px] mx-auto">
            Are you sure you want to permanently delete this term? This action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95"
              onClick={handleConfirmDelete}
            >
              Delete Term
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Terms;
